import { Line } from "react-chartjs-2";

import { CHART_SENTIMENT_COLORS } from "@/utils";

import { BaseWidget } from "./BaseWidget";
import {
  datasetLineOptions,
  eventPropsForTimeline,
  lineOptions,
} from "./chart_options";
import { sumArray } from "./helpers";

const hasData = (data) =>
  data && data.map((d) => (d.sentiment || []).length).reduce(sumArray, 0) > 0;

const SentimentLineWidget = (props) => {
  let labels = [];
  const datasets = [];

  if (hasData(props.data)) {
    const DATE_INDEX = 0,
      VALUE_INDEX = 1,
      POSITIVE_INDEX = 0,
      NEGATIVE_INDEX = 1,
      NEUTRAL_INDEX = 2;

    let allPos = {},
      allNeg = {},
      allNeu = {};

    // ugly but works: create objects with all neg, pos and neutral values
    // using date as key since different reports can have different dates
    props.data.forEach((reportData) => {
      (reportData["sentiment"] || []).forEach((row) => {
        let date = row[DATE_INDEX];

        if (!allPos[date]) allPos[date] = [];
        allPos[date].push(row[VALUE_INDEX][POSITIVE_INDEX]);

        if (!allNeg[date]) allNeg[date] = [];
        allNeg[date].push(row[VALUE_INDEX][NEGATIVE_INDEX]);

        if (!allNeu[date]) allNeu[date] = [];
        allNeu[date].push(row[VALUE_INDEX][NEUTRAL_INDEX]);
      });
    });

    labels = Object.keys(allPos);

    let positiveValues = [],
      negativeValues = [],
      neutralValues = [];

    // loop over all neg/pos/neutral values, sum them and convert to percentage
    labels.forEach((date) => {
      let pos = allPos[date].reduce(sumArray, 0),
        neg = allNeg[date].reduce(sumArray, 0),
        neu = allNeu[date].reduce(sumArray, 0),
        sum = pos + Math.abs(neg) + neu;

      const posPercent = Math.ceil((pos / sum) * 100);
      const negPercent = Math.ceil((neg / sum) * 100);
      const neuPercent = 100 - posPercent - Math.abs(negPercent);

      positiveValues.push(posPercent || 0); // convert any NaN to 0
      negativeValues.push(negPercent || 0);
      neutralValues.push(neuPercent || 0);
    });

    datasets.push(
      {
        label: L.CUSTOMERS_SENTIMENT_GRAPH_POSITIVE,
        data: positiveValues,
        ...datasetLineOptions(CHART_SENTIMENT_COLORS.positive),
      },
      {
        label: L.CUSTOMERS_SENTIMENT_GRAPH_NEGATIVE,
        data: negativeValues,
        ...datasetLineOptions(CHART_SENTIMENT_COLORS.negative),
      },
      {
        label: L.CUSTOMERS_SENTIMENT_GRAPH_NEUTRAL,
        data: neutralValues,
        ...datasetLineOptions(CHART_SENTIMENT_COLORS.neutral),
        showLine: false,
        pointRadius: 0,
        pointHoverRadius: 0,
      }
    );
  }

  const chartData = {
    labels: labels,
    datasets: datasets,
  };

  const options = lineOptions(true, props.size);

  options.scales.y = {
    ...options.scales.y,
    stacked: false,
    ticks: {
      ...options.scales.y.ticks,
      callback: (v) => `${v}%`,
      beginAtZero: true,
      suggestedMin: -100,
      suggestedMax: 100,
    },
    grid: {
      ...options.scales.y.grid,
      display: true,
      drawBorder: true,
      color: (context) => {
        if (context.tick.value === 0) {
          return "rgba(0, 0, 0, 0.125)";
        }
        return "transparent";
      },
    },
  };
  delete options.scales.y.ticks.min;

  const neutralIndex = 2;
  const filterOutNeutral = (item) => item.datasetIndex != neutralIndex;

  options.plugins.legend.labels = {
    usePointStyle: true,
    filter: filterOutNeutral,
  };

  // options.tooltips.filter = filterOutNeutral;
  options.plugins.tooltip = options.plugins.tooltip || {};
  options.plugins.tooltip.callbacks = options.plugins.tooltip.callbacks || {};
  options.plugins.tooltip.callbacks.label = ({
    formattedValue,
    datasetIndex,
  }) => {
    const label = datasets[datasetIndex].label;
    return `${label}: ${Math.abs(formattedValue)}%`;
  };

  return (
    <BaseWidget {...props} hasData={hasData(props.data)}>
      <Line
        data={chartData}
        options={{
          ...options,
          ...eventPropsForTimeline(chartData, props, "sentiment"),
        }}
      />
    </BaseWidget>
  );
};

export { SentimentLineWidget };
